import {
	ChangeDetectorRef,
	Component,
	computed,
	DestroyRef,
	effect,
	ElementRef,
	inject,
	Injector,
	OnInit,
	signal
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { auditTime, filter, take } from 'rxjs';
import * as dayjs from 'dayjs';
import { Activity } from '@go-modules/interfaces/activity';
import { ActivityEditAccessibilityAnnouncerService } from '@go-modules/components/sidepanels/activity-editor/activity-edit-accessibility-announcer.service';
import { ActivityFormService } from '@go-modules/components/sidepanels/activity-editor/activity-form/activity-form.service';
import type { ActivityFormValue } from '@go-modules/components/sidepanels/activity-editor/activity-form/activity-form-config';
import { NgxActivityEditComponent } from '@go-modules/components/sidepanels/activity-editor/ngx-activity-edit/ngx-activity-edit.component';
import { ConfirmDialogComponent } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import { ParticipantRubricSettingComponent } from '../participant-rubric-setting/participant-rubric-setting.component';
import { ActivityFactory, ActivityModel } from 'go-modules/models/activity/activity.schema';
import { activityToken } from 'go-modules/models/activity/activity.factory';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { NgxTaskService } from '@go-modules/services/task/task.service';
import { Task as ActivityTask } from '@go-modules/interfaces/task';
import { ACTIVITY_EDITOR_CONTEXT } from '../activity-editor-context';

@Component({
	selector: 'activity-details',
	standalone: true,
	imports: [
		NgxActivityEditComponent,
		ParticipantRubricSettingComponent,
		AsyncPipe,
		TranslateModule
	],
	providers: [
		ActivityFormService,
		ActivityEditAccessibilityAnnouncerService
	],
	templateUrl: './activity-details.component.html',
	styleUrls: ['./activity-details.component.scss']
})
export class ActivityDetailsComponent implements OnInit {
	public readonly activityFormService = inject(ActivityFormService);
	public readonly accessibilityAnnouncer = inject(ActivityEditAccessibilityAnnouncerService);
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly destroyRef = inject(DestroyRef);
	private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
	private readonly injector = inject(Injector);
	private readonly matDialog = inject(MatDialog);
	private readonly ngxActivityService = inject(NgxActivityService);
	private readonly taskService = inject(NgxTaskService);
	private readonly translate = inject(TranslateService);
	private readonly activityModelFactory = inject(activityToken) as ActivityFactory & {
		model(data?: unknown, skipCache?: boolean): ActivityModel;
	};
	private readonly editorContext = inject(ACTIVITY_EDITOR_CONTEXT);

	public activityModel: ActivityModel | null = null;
	public readonly showMasterActivityToggle = this.editorContext.showMasterActivityToggle;
	public readonly isActivityNameReadonly = this.editorContext.isActivityNameReadonly ?? signal(false);
	public readonly isLibraryEnvironment = computed(() => this.editorContext.environment().name === 'library');

	private initializedActivityKey: number | string | null = null;
	private isInitializingForm = false;

	public ngOnInit (): void {
		effect(() => {
			const activity = this.editorContext.activity();
			if (activity == null) {
				return;
			}

			const activityKey = activity.activity_id ?? 'draft';
			if (this.initializedActivityKey === activityKey && this.activityModel != null) {
				return;
			}

			this.initializeForm(activity, activityKey);
		}, { injector: this.injector });

		this.activityFormService.getForm().valueChanges
			.pipe(
				auditTime(0),
				filter(() => this.activityFormService.getForm().dirty),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(() => this.syncActivityFromForm());

		this.editorContext.registerAdditionalSettingsValidator({
			validateAdditionalSettingsForm: () => this.validateActivityDetailsForm(),
			focusFirstInvalidField: () => this.focusFirstInvalidField()
		});
	}

	public onMasterActivityToggled (isEnabled: boolean): void {
		const activity = this.editorContext.activity();
		if (activity?.activity_id == null) {
			return;
		}

		this.ngxActivityService.getChildrenCount(activity.activity_id)
			.pipe(take(1), takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (result.activities <= 0) {
					return;
				}

				this.matDialog.open(ConfirmDialogComponent, {
					data: {
						title: this.translate.instant(isEnabled
							? 'modal-activity-enable-master-warning_edit-activity'
							: 'modal-activity-disable-master-warning_edit-activity'),
						message: this.translate.instant(isEnabled
							? 'modal-activity-enable-master-warning_warning-text'
							: 'modal-activity-disable-master-warning_warning-text', {
							folders: result.activities,
							videos: result.sessions
						}),
						confirmText: this.translate.instant('common_continue')
					}
				}).afterClosed()
					.pipe(take(1), takeUntilDestroyed(this.destroyRef))
					.subscribe((confirmed) => {
						if (!confirmed) {
							this.activityFormService.getControl('parent_child_sync')?.setValue(!isEnabled);
						}
					});
			});
	}

	private initializeForm (activity: Activity, activityKey: number | string): void {
		this.isInitializingForm = true;
		this.initializedActivityKey = activityKey;
		this.activityModel = this.activityModelFactory.model(structuredClone(activity), true);
		this.activityFormService.initializeForm(this.activityModel);
		this.activityFormService.markFormAsPristine();
		this.isInitializingForm = false;
		this.cdr.markForCheck();
	}

	private validateActivityDetailsForm (): boolean {
		this.syncActivityFromForm();

		if (this.activityFormService.isFormValid()) {
			return true;
		}

		this.activityFormService.getForm().markAllAsTouched();
		this.cdr.markForCheck();
		return false;
	}

	private focusFirstInvalidField (): void {
		const invalidControl = this.elementRef.nativeElement.querySelector<HTMLElement>(
			'.ng-invalid input, .ng-invalid textarea, .ng-invalid mat-select, input.ng-invalid, textarea.ng-invalid, mat-select.ng-invalid'
		);

		invalidControl?.focus();
	}

	private syncActivityFromForm (): void {
		if (this.isInitializingForm || this.activityModel == null) {
			return;
		}

		const activity = this.editorContext.activity();
		if (activity == null) {
			return;
		}

		const formValue = this.activityFormService.getForm().getRawValue() as ActivityFormValue;
		// Template selection updates activityModel before the APED store is synchronized.
		// During that transition, keep the model's template-forced task settings instead
		// of overwriting them with values from the previous activity snapshot.
		const isChangingActivityType = this.activityFormService.getControl('activity_template_id')?.dirty === true &&
			formValue.activity_template_id !== activity.activity_template_id;
		Object.assign(this.activityModel, {
			...activity,
			...formValue,
			...this.taskSettingsForCreateVideo(!isChangingActivityType),
			template: this.activityModel.template ?? activity.template
		});
		this.activityModel.applyTemplate(this.activityModel.template);
		const activityModelState = structuredClone(this.activityModel) as unknown as Partial<Activity>;

		const activityUpdate = {
			...activityModelState,
			available_at: this.serializeDate(formValue.available_at),
			due_at: this.serializeDate(formValue.due_at),
			template: activityModelState.template ?? activity.template
		};

		this.editorContext.updateActivity(activityUpdate as unknown as Activity);
		this.syncTasksForActivity(activityUpdate as unknown as Activity);
	}

	private syncTasksForActivity (activity: Activity): void {
		const currentTasks = this.editorContext.tasks();
		const reconciledTasks = this.reconcileTasksForActivity(currentTasks, activity);

		if (JSON.stringify(reconciledTasks) === JSON.stringify(currentTasks)) {
			return;
		}

		this.editorContext.updateTasks(reconciledTasks);
	}

	private reconcileTasksForActivity (tasks: ActivityTask[], activity: Activity): ActivityTask[] {
		const compatibleTaskTypes = new Set(this.taskService.getCompatibleTasks(activity).map((task) => task.type));

		// Remove tasks the selected template no longer supports, but do not add newly
		// compatible defaults. An absent task may be an instructor customization.
		return tasks
			.filter((task) => compatibleTaskTypes.has(task.type))
			.sort((left, right) => left.sort_order - right.sort_order)
			.map((task, index) => ({ ...task, sort_order: index + 1 }));
	}

	private serializeDate (value: Date | null): string | null {
		return value == null ? null : dayjs(value).utc().format('YYYY-MM-DD HH:mm:ss');
	}

	/**
	 * These settings are either not present or are `false` by default in `formValue`.
	 * Additionally, `formValue` isn't updated when the checkboxes are toggled, but `activity` is.
	 */
	private taskSettingsForCreateVideo (preserveContextSettings: boolean) {
		const activity = preserveContextSettings
			? this.editorContext.activity()
			: this.activityModel;
		return {
			live_session_enabled: activity.live_session_enabled,
			is_conversation: activity.is_conversation,
			time_limit: activity.time_limit,
			has_single_recording_attempt: activity.has_single_recording_attempt,
			is_slides_enabled: activity.is_slides_enabled,
			is_video_share_enabled: activity.is_video_share_enabled
		};
	}
}
